<template>
    <div class="modal fade" id="openActionModal" tabindex="-1" aria-labelledby="openActionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <form v-if="selectedAction" class="modal-content" @submit.prevent="handleSubmit">
                <div class="modal-header text-bg-primary">
                    <h5 class="modal-title" id="openActionModalLabel">{{ $t('actionModal.title') }}</h5>
                    <button type="button" class="btn-close text-light" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body px-0">
                    <!-- Action à executer -->
                    <div id="action-container" class="row p-2 p-md-3">
                        <div class="col-12">
                            <h4 class="text-primary mt-0">{{ $t('actionModal.actionType') }}</h4>
                            <select class="form-select mt-2 border-primary" v-model="selectedAction.action_type" required>
                                <option disabled value="">{{ $t('actionModal.selectAction') }}</option>
                                <option value="control_doors">{{ $t('actionModal.controlDoors') }}</option>
                                <option value="control_outputs">{{ $t('actionModal.controlOutputs') }}</option>
                                <option v-if="remote_outputs.length > 0" value="control_remote_outputs">{{ $t('actionModal.controlRemoteOutputs') }}</option>
                                <option value="send_email">{{ $t('actionModal.sendEmail') }}</option>
                            </select>
                        </div>
                        <!-- [IF] action = Piloter une ou plusieurs portes -->
                        <div v-if="selectedAction.action_type === 'control_doors'">
                            <div class="col-12 mt-2 mb-2">
                                <label for="doors-selection" class="form-label">{{ $t('actionModal.selectDoors') }}</label>
                                <multiselect
                                    id="doors-selection"
                                    v-model="selected_doors"
                                    track-by="id"
                                    :options="doors"
                                    :multiple="true"
                                    required
                                    :placeholder="$t('actionModal.selectDoorsPlaceholder')"
                                    :selectLabel="$t('multiselect.selectLabel')"
                                    :deselectLabel="$t('multiselect.deselectLabel')"
                                    :selectedLabel="$t('multiselect.selectedLabel')"
                                    :clear-on-select="true">
                                </multiselect>
                            </div>
                            <div class="col-12 mb-2">
                                <label for="piloter-porte-action" class="form-label">{{ $t('actionModal.mode') }}</label>
                                <select class="form-select" v-model="selectedAction.control_doors.mode">
                                    <option value="Open">{{ $t('actionModal.doorMode.open') }}</option>
                                    <option value="ForcedOpen">{{ $t('actionModal.doorMode.forcedOpen') }}</option>
                                    <option value="Close">{{ $t('actionModal.doorMode.close') }}</option>
                                    <option value="ForcedClose">{{ $t('actionModal.doorMode.forcedClose') }}</option>
                                    <option value="StopForcedMode">{{ $t('actionModal.doorMode.stopForcedMode') }}</option>
                                </select>
                            </div>
                        </div>
                        <!-- [IF] action = Piloter une ou plusieurs sorties -->
                        <div v-if="selectedAction.action_type === 'control_outputs'">
                            <div class="col-12 mt-2 mb-2">
                                <label for="outputs-selection" class="form-label">{{ $t('actionModal.selectOutputs') }}</label>
                                <multiselect
                                    id="outputs-selection"
                                    v-model="selected_outputs"
                                    track-by="output_number"
                                    label="label"
                                    :options="outputs"
                                    :multiple="true"
                                    required
                                    :placeholder="$t('actionModal.selectOutputsPlaceholder')"
                                    :selectLabel="$t('multiselect.selectLabel')"
                                    :deselectLabel="$t('multiselect.deselectLabel')"
                                    :selectedLabel="$t('multiselect.selectedLabel')"
                                    :clear-on-select="true">
                                </multiselect>
                            </div>
                            <div class="col-12 mb-2">
                                <label for="piloter-sortie-action" class="form-label">{{ $t('actionModal.mode') }}</label>
                                <select class="form-select" id="piloter-sortie-action" v-model="selectedAction.control_outputs.mode">
                                    <option value="Activate">{{ $t('actionModal.outputMode.activate') }}</option>
                                    <option value="ActiveFor">{{ $t('actionModal.outputMode.activateFor') }}</option>
                                    <option value="Deactivate">{{ $t('actionModal.outputMode.deactivate') }}</option>
                                    <option value="Invert">{{ $t('actionModal.outputMode.invert') }}</option>
                                </select>
                            </div>
                            <div v-if="selectedAction.control_outputs.mode === 'ActiveFor'" class="col-12">
                                <TempoWithUnit :label="$t('common.delay')" v-model:value="selectedAction.control_outputs.delay.value" v-model:unit="selectedAction.control_outputs.delay.unit" :min="1" :max="255"></TempoWithUnit>
                            </div>
                        </div>
                        <!-- [IF] action = Piloter une ou plusieurs sorties externes -->
                        <div v-if="selectedAction.action_type === 'control_remote_outputs'">
                            <div class="col-12 mt-2 mb-2">
                                <label for="remote-outputs-selection" class="form-label">{{ $t('actionModal.selectOutputs') }}</label>
                                <multiselect
                                    id="remote-outputs-selection"
                                    v-model="selected_remote_outputs"
                                    track-by="output_id"
                                    label="label"
                                    :options="remote_outputs"
                                    :multiple="true"
                                    required
                                    :placeholder="$t('actionModal.selectOutputsPlaceholder')"
                                    :selectLabel="$t('multiselect.selectLabel')"
                                    :deselectLabel="$t('multiselect.deselectLabel')"
                                    :selectedLabel="$t('multiselect.selectedLabel')"
                                    :clear-on-select="true">
                                </multiselect>
                            </div>
                            <div class="col-12 mb-2">
                                <label for="piloter-sortie-externe-action" class="form-label">{{ $t('actionModal.mode') }}</label>
                                <select class="form-select" id="piloter-sortie-externe-action" v-model="selectedAction.control_remote_outputs.mode">
                                    <option value="Activate">{{ $t('actionModal.outputMode.activate') }}</option>
                                    <option value="ActiveFor">{{ $t('actionModal.outputMode.activateFor') }}</option>
                                    <option value="Deactivate">{{ $t('actionModal.outputMode.deactivate') }}</option>
                                    <option value="Invert">{{ $t('actionModal.outputMode.invert') }}</option>
                                </select>
                            </div>
                            <div v-if="selectedAction.control_remote_outputs.mode === 'ActiveFor'" class="col-12">
                                <TempoWithUnit :label="$t('common.delay')" v-model:value="selectedAction.control_remote_outputs.delay.value" v-model:unit="selectedAction.control_remote_outputs.delay.unit" :min="1" :max="255"></TempoWithUnit>
                            </div>
                        </div>
                        <!-- [IF] action = Envoyer un email -->
                        <div v-if="selectedAction.action_type === 'send_email'" class="mt-2">
                            <label for="emails-input" class="form-label">{{ $t('actionModal.emails') }}</label>
                            <multiselect
                                id="emails-selection"
                                v-model="selectedAction.send_email.recipients"
                                :options="[]"
                                :multiple="true"
                                :close-on-select="false"
                                :placeholder="$t('actionModal.emailsPlaceholder')"
                                :clear-on-select="true"
                                :taggable="true"
                                @tag="addEmail"
                                :showNoOptions="false"
                                :tagPlaceholder="$t('actionModal.emailTagPlaceholder')"
                                class="mb-2"
                                required
                            >
                            </multiselect>
                            <label for="objet" class="form-label">{{ $t('actionModal.emailSubject') }}</label>
                            <input type="text" id="objet" class="form-control mb-2" :placeholder="$t('actionModal.emailSubjectPlaceholder')" v-model="selectedAction.send_email.subject" maxlength="64" required>
                            <label for="message" class="form-label">{{ $t('actionModal.emailMessage') }}</label>
                            <textarea id="message" class="form-control" :placeholder="$t('actionModal.emailMessagePlaceholder')" rows="5" v-model="selectedAction.send_email.body" maxlength="255" required ></textarea>
                        </div>
                    </div>
                </div>
                <div class="modal-footer justify-content-between">
                    <button type="button" class="btn btn-secondary" ref="closeButton" data-bs-dismiss="modal">{{ $t('common.cancel') }}</button>
                    <span v-if="errorMessage" class="custom-error-msg">{{ errorMessage }}</span>
                    <button type="submit" class="btn btn-primary">{{ $t('common.add') }}</button>
                </div>
            </form>
        </div>
    </div>

</template>

<script>
import { useClientStore } from '@/stores/clientStore';
import { showToast } from '@/services/toast';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Multiselect from 'vue-multiselect'
import TempoWithUnit from '@/components/tempoWithUnit.vue';
import Action from '@/models/action'

export default {
    name: 'ActionModal',
    components: {
        Multiselect,
        TempoWithUnit,
    },
    props: {
        hub: {
            type: Object,
            required: true
        }
    },
    computed: {
        doors() {
            const clientStore = useClientStore();
            return clientStore.getDoors();
        },
        outputs() {
            const clientStore = useClientStore();
            return clientStore.getOutputs();
        },
        remote_outputs() {
            const clientStore = useClientStore();
            return clientStore.getRemoteOutputs();
        }
    },
    setup(props, { emit }) {
        const { t } = useI18n();
        const closeButton = ref(null);
        const selectedAction = ref(null);
        const errorMessage = ref('');

        const selected_doors = computed({
            get() {
                const clientStore = useClientStore();
                return clientStore.getDoors(selectedAction.value.control_doors.doors);
            },
            set(ids) {
                selectedAction.value.control_doors.doors = ids.map(e => e.id);
            }
        });

        const selected_outputs = computed({
            get() {
                const clientStore = useClientStore();
                return clientStore.getOutputs(selectedAction.value.control_outputs.outputs);
            },
            set(outputs) {
                selectedAction.value.control_outputs.outputs = outputs.map(o => o.output_number);
            }
        });

        const selected_remote_outputs = computed({
            get() {
                const clientStore = useClientStore();
                return clientStore.getRemoteOutputs(selectedAction.value.control_remote_outputs.remote_outputs);
            },
            set(outputs) {
                selectedAction.value.control_remote_outputs.remote_outputs = outputs;
            }
        });

        const setAction = (action) => {
            if(action.id === -1) {
                selectedAction.value = action;
            } else {
                selectedAction.value = Action.constructFromObject(action);
            }
            errorMessage.value = "";
        };

        const handleSubmit = () => {
            emit('onActionUpdated', selectedAction.value);
            closeButton.value.click();
        };

        const addEmail = (newEmail) => {
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

            if (!emailRegex.test(newEmail)) {
                showToast('error', t('common.invalidEmail'));
                return;
            }
            if(selectedAction.value.send_email.recipients.findIndex(mail => mail === newEmail) == -1) {
                selectedAction.value.send_email.recipients.push(newEmail);
            }
        };

        return {
            closeButton,
            selectedAction,
            handleSubmit,
            setAction,
            addEmail,
            errorMessage,
            selected_doors,
            selected_outputs,
            selected_remote_outputs,
        }
    },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
.multiselect__placeholder {
    font-size: 0.9rem;
    color: #8a969c;
    opacity: 1;
    padding-left: 0;
}
.multiselect__input:focus::placeholder {
    font-size: 0.9rem;
    color: #8a969c;
    opacity: 1;
    padding-left: 0;
}
.multiselect__input {
    padding-left: 0;
}
.multiselect__tags {
    padding-left: 0.9rem;
}

.custom-error-msg {
  color: red;
  font-size: 14px;
}
</style>
